import {
  LOCK_PRESETS,
  MS_PER_DAY,
  XP_RATE_FORMULA,
  multiplierForDays,
  roundXp,
  xpAtMaturity,
} from "../../config/supernovaXp";
import { SUPERNOVA_TOKENS } from "./supernovaContracts";

/**
 * supernovaStakePreview — the pure model behind the stake modal's live preview.
 *
 * No React, no formatting, no I/O. It takes what the member has picked (type,
 * quantity, lock) plus what the chain said they hold, and returns both the
 * figures to show and whether the form is submittable. The modal renders it; it
 * never computes.
 *
 * EVERY NUMBER COMES FROM config/supernovaXp.js. Nothing is re-derived here —
 * the multiplier table, the base rate and the maturity figure are all the
 * config's, so the preview cannot drift from the model the engine mirrors.
 *
 * WHAT `xpAtMaturity` IS: the model's ceiling for this lot if it is held to
 * term under today's locked parameters. It is NOT a projection, a promise, a
 * yield, or a return, and the caption that accompanies it on screen must keep
 * saying so — XP accrues daily, pro-rata, and nothing is credited up front.
 *
 * INVALID IN, NOTHING OUT. When the form isn't valid the XP figures come back
 * `null`, not 0. A zero here would render as "staking this gets you nothing",
 * which is a statement about the model rather than about an unfilled field.
 */

/* THERE IS NO DEFAULT LOCK, DELIBERATELY. A lock is the one choice on this form
   a member cannot undo, so it is never pre-filled and never falls back: omit it
   and the preview comes back NO_LOCK, which keeps the Stake button disabled
   until a chip is explicitly picked. A default here would let someone sign a
   duration they never chose. */

export const STAKE_INVALID = {
  NO_TYPE: "NO_TYPE",
  NO_LOCK: "NO_LOCK",
  QTY_NOT_A_COUNT: "QTY_NOT_A_COUNT",
  QTY_TOO_LOW: "QTY_TOO_LOW",
  QTY_ABOVE_BALANCE: "QTY_ABOVE_BALANCE",
  BALANCE_UNKNOWN: "BALANCE_UNKNOWN",
};

const isCount = (n) => typeof n === "number" && Number.isInteger(n) && n >= 0;

/** The preset for a lock length, or undefined. Locks are presets only — a free
    field would let a member contract a duration the tier table doesn't cover. */
export function presetForDays(lockDays) {
  return LOCK_PRESETS.find((p) => p.days === lockDays);
}

/** Chip label for a preset: "30d", "180d", "1y", "3y". */
export function lockChipLabel(days) {
  return days >= 365 && days % 365 === 0 ? `${days / 365}y` : `${days}d`;
}

/**
 * The six duration chips, each carrying its own multiplier so the label and the
 * arithmetic can't disagree. `edge` marks the ends of the range for the "min"
 * and "max" hints the mockup shows.
 */
export const LOCK_CHIPS = LOCK_PRESETS.map((preset, i) => ({
  ...preset,
  chipLabel: lockChipLabel(preset.days),
  edge: i === 0 ? "min" : i === LOCK_PRESETS.length - 1 ? "max" : null,
}));

/**
 * The member-facing formula, with their own numbers substituted in. Shown so
 * the preview is checkable rather than magic.
 */
export function stakeFormulaLine(qty, lockDays, multiplier) {
  const nfts = qty === 1 ? "1 NFT" : `${qty} NFTs`;
  const days = lockDays === 1 ? "1 day" : `${lockDays} days`;
  return `(${XP_RATE_FORMULA}) XP per NFT per day × ${nfts} × ${days} × ${multiplier.toFixed(2)} lock multiplier`;
}

/**
 * @typedef {Object} StakePreview
 * @property {boolean} valid          may this be submitted?
 * @property {string|null} invalidReason  a STAKE_INVALID code
 * @property {number|null} tokenId
 * @property {string|null} tokenName
 * @property {number|null} maxQty     NFTs of this type in the wallet; null when
 *                                    the balance read isn't ready
 * @property {number} qty
 * @property {number|null} lockDays   null until a chip is picked
 * @property {string|null} lockLabel  "90 days" / "3 years"
 * @property {string|null} lockChipLabel  "90d" / "3y"
 * @property {number|null} multiplier the lock's XP rate multiplier
 * @property {number|null} xpAtMaturity   model ceiling if held to term
 * @property {number|null} xpPerDay   XP this lot accrues per whole day
 * @property {number} slots           strategy slots this stake would unlock
 * @property {number|null} unlockAtMs
 * @property {string|null} formula
 */

/**
 * Build the live preview.
 *
 * @param {Object} input
 * @param {number|null} input.tokenId   ERC-1155 id 7–10
 * @param {number} input.qty            NFTs to lock
 * @param {number|null} input.lockDays  one of LOCK_PRESETS; null until the
 *        member picks a chip, which blocks the form rather than defaulting
 * @param {number|null} input.maxQty    balance of this type IN THE WALLET; null
 *        when that read failed or isn't connected — which blocks the form,
 *        because we can't offer to lock NFTs we can't confirm they hold
 * @param {number|Date} [input.nowMs]   clock, injected so this stays pure
 * @returns {StakePreview}
 */
export function buildStakePreview({
  tokenId = null,
  qty = 1,
  lockDays = null,
  maxQty = null,
  nowMs = Date.now(),
} = {}) {
  const token = SUPERNOVA_TOKENS.find((t) => t.id === tokenId) || null;
  /* "No chip picked yet" is its own state — not a zero-day lock, and not a
     silent fallback to some preset. Everything derived from the lock stays null
     until there is one. */
  const chosen = typeof lockDays === "number" && Number.isFinite(lockDays) && lockDays > 0;
  const preset = chosen ? presetForDays(lockDays) : undefined;
  const multiplier = preset
    ? preset.multiplier
    : chosen
      ? multiplierForDays(lockDays)
      : null;
  const now = nowMs instanceof Date ? nowMs.getTime() : nowMs;

  const base = {
    tokenId: token ? token.id : null,
    tokenName: token ? token.name : null,
    maxQty: isCount(maxQty) ? maxQty : null,
    qty,
    lockDays: chosen ? lockDays : null,
    lockLabel: preset ? preset.label : chosen ? `${lockDays} days` : null,
    lockChipLabel: chosen ? lockChipLabel(lockDays) : null,
    multiplier,
    slots: isCount(qty) ? qty : 0,
    /* The unlock date doesn't depend on quantity, so it stays truthful even
       while the quantity field is mid-edit. */
    unlockAtMs: preset ? now + lockDays * MS_PER_DAY : null,
    formula: chosen
      ? stakeFormulaLine(isCount(qty) && qty > 0 ? qty : 1, lockDays, multiplier)
      : null,
    xpAtMaturity: null,
    xpPerDay: null,
  };

  const invalid = (invalidReason) => ({ ...base, valid: false, invalidReason });

  if (!token) return invalid(STAKE_INVALID.NO_TYPE);
  if (!preset) return invalid(STAKE_INVALID.NO_LOCK);
  if (!isCount(qty)) return invalid(STAKE_INVALID.QTY_NOT_A_COUNT);
  if (qty < 1) return invalid(STAKE_INVALID.QTY_TOO_LOW);
  /* A null balance is NOT "you have none" — it's "we couldn't read it", and the
     difference decides whether we let someone sign a transaction. */
  if (base.maxQty === null) return invalid(STAKE_INVALID.BALANCE_UNKNOWN);
  if (qty > base.maxQty) return invalid(STAKE_INVALID.QTY_ABOVE_BALANCE);

  const lot = { qty, lockDays, stakedAtMs: now };
  const maturityXp = xpAtMaturity(lot);

  return {
    ...base,
    valid: true,
    invalidReason: null,
    xpAtMaturity: roundXp(maturityXp, 6),
    xpPerDay: roundXp(maturityXp / lockDays, 6),
  };
}

/**
 * Member-safe copy for why the form can't be submitted. Returns null when it
 * can — the modal shows nothing rather than a green "looks good".
 */
export function stakeBlockedMessage(preview) {
  if (!preview || preview.valid) return null;
  switch (preview.invalidReason) {
    case STAKE_INVALID.NO_TYPE:
      return "Choose which Supernova you'd like to stake.";
    case STAKE_INVALID.NO_LOCK:
      return "Choose a lock duration.";
    case STAKE_INVALID.QTY_NOT_A_COUNT:
    case STAKE_INVALID.QTY_TOO_LOW:
      return "Enter how many you'd like to stake — at least 1.";
    case STAKE_INVALID.BALANCE_UNKNOWN:
      return (
        "We can't read how many of this type are in your wallet right now, so " +
        "we won't offer to lock them. Nothing on-chain has changed."
      );
    case STAKE_INVALID.QTY_ABOVE_BALANCE:
      return preview.maxQty === 0
        ? "None of this type is in your wallet to stake."
        : `You have ${preview.maxQty.toLocaleString("en-US")} of this type in your wallet.`;
    default:
      return "Check the details above before signing.";
  }
}

export default buildStakePreview;
